import {
    Grid, Tab, Tabs, Typography
} from '@mui/material';
import { ActionButton, CancelButtonGrey } from '@tassat/button';
import {
    ActionTypes,
    TransactionStatusCode,
    TransactionStatusCodeMap,
    TransactionTypeChar,
    TransactionTypeString,
    doesWorkflowRejectCodeExist,
    transactionTypeMap
} from '@tassat/common-definitions';
import { AccountWithdrawalDataRecord } from '@tassat/fred-definitions';
import { TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import { Edit as EditIcon } from '@tassat/icon';
import { TabPanel } from '@tassat/layout';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React from 'react';

import { getTransactions } from 'Containers/admin/forms/helpers/walletFormFn';
import { FIRM_VIEW_WITHDRAWAL_INFO_TAB } from 'Containers/admin/pages/firms/firmView';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAssociatedWalletsbyAccountWithdrawalDataId } from 'Selectors/accountsWithdrawalRelationships';
import { getCurrentRole } from 'Selectors/common';
import { getWithdrawalInstructionById } from 'Selectors/withdrawalInstructions';
import WithdrawalInstructionDetails from 'SharedComponents/withdrawalInstructions/withdrawalInstructionDetails';

import FirmWithdrawalWallets from './firmWithdrawalWallets';

import { FirmWithdrawalDetailsProps } from '.';

export function FirmWithdrawalDetails(props: FirmWithdrawalDetailsProps) {
    const {
        classes,
        firm,
        editWithdrawalInstruction,
        history,
        location,
        match
    } = props;
    const userRole = useAppSelector(getCurrentRole);
    const withdrawalInstruction = useAppSelector((state) => {
        return getWithdrawalInstructionById(state, firm.FirmId, match.params.accountWithdrawalDataId);
    });
    const associatedWallets = useAppSelector((state) => {
        return getAssociatedWalletsbyAccountWithdrawalDataId(state, firm.FirmId, withdrawalInstruction?.AccountWithdrawalDataId);
    });
    const [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ] =
    React.useReducer(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => {
        return {
            confirmationWorkflowState,
            dispatchConfirmationWorkflowAction
        };
    }, [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);
    const [ currentTab, setCurrentTab ] = React.useState<number>(0);

    React.useEffect(() => {
        if (!withdrawalInstruction || (withdrawalInstruction &&
            (Object.keys(withdrawalInstruction).length === 0 || withdrawalInstruction.IsDeleted))) {
            history.push(`/admin/firms/${firm?.FirmId}`, {
                ...location.state,
                tab: FIRM_VIEW_WITHDRAWAL_INFO_TAB
            });
        }
    }, [withdrawalInstruction]);

    const deleteWithdrawalInstructionValidation = async (withdrawalInstruction: AccountWithdrawalDataRecord): Promise<boolean> => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: withdrawalInstruction,
                actionType: ActionTypes.delete,
                confirmTitle: 'Confirm Deleting Withdrawal Instruction',
                confirmBody: `Please confirm deleting the withdrawal instruction: ${withdrawalInstruction.Description}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const deleteWithdrawalInstruction = async (withdrawalInstruction: AccountWithdrawalDataRecord) => {
        const promises = associatedWallets.map(wallet => {
            return getTransactions({
                Condition: `wallet|EQ|${wallet?.Account}:tranType|EQ|${TransactionTypeChar.withdrawal.toUpperCase()}`,
                Sort: 'auditTime|DESC',
                PageNo: 1,
                RecordPerPage: 10
            });
        });

        try {
            const responses = await Promise.all(promises);
            const isAnyWalletsInPendingStatus = responses.some((response) => {
                return response.data?.transactions.some((transaction: TransactionHistoryRecord) => {
                    return transaction.UIStatus === TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.displayValue &&
                        transactionTypeMap(transaction.TransType) === TransactionTypeString.withdrawal;
                });
            });
            if (isAnyWalletsInPendingStatus) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: 'Failed to Delete Withdrawal Instruction',
                        failureBody: 'Cannot delete withdrawal instruction due to pending withdrawals'
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else {
                try {
                    const response = await Worker.getInstance()
                        .deleteWithdrawalInstruction(withdrawalInstruction.AccountWithdrawalDataId);

                    if (response.data && response.data.Status === 48) {
                        //No neeed to close alert and form modal as we are redirecting
                        history.push(`/admin/firms/${withdrawalInstruction.FirmId}`, {
                            ...location.state,
                            tab: FIRM_VIEW_WITHDRAWAL_INFO_TAB
                        });
                    } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                        const serverErrorAction: ConfirmationWorkflowAction = {
                            type: 'SERVER_ERROR',
                            workflowConfiguration: {
                                failureTitle: 'Failed to Delete Withdrawal Instruction',
                                failureBody: `Could not delete withdrawal instruction ${withdrawalInstruction.Description}.`
                            }
                        };
                        dispatchConfirmationWorkflowAction(serverErrorAction);
                    }
                } catch (error) {
                    const serverErrorAction: ConfirmationWorkflowAction = {
                        type: 'SERVER_ERROR',
                        workflowConfiguration: {
                            failureTitle: 'Failed to Delete Withdrawal Instruction',
                            failureBody: `Something went wrong deleting withdrawal instruction ${withdrawalInstruction.Description}.`
                        }
                    };
                    dispatchConfirmationWorkflowAction(serverErrorAction);
                }
            }
        } catch (e) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: 'Failed to Delete Withdrawal Instruction',
                    failureBody: 'Something went wrong checking there are pending withdrawals'
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        //set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: withdrawalInstruction,
                setSelectedItem: () => withdrawalInstruction,
                preActionValidationAndConfig: { [ActionTypes.delete]: deleteWithdrawalInstructionValidation },
                onConfirm: { [ActionTypes.delete]: deleteWithdrawalInstruction },
                useTimeout: true
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, [associatedWallets]);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <Grid
                className={classes.root}
                container
                direction='column'
                justifyContent='center'
                spacing={0}
            >
                <Grid
                    className={classes.cancelButton}
                    item
                >
                    <CancelButtonGrey
                        onClick={() => history.push(`/admin/firms/${withdrawalInstruction.FirmId}`, {
                            ...location.state,
                            tab: FIRM_VIEW_WITHDRAWAL_INFO_TAB
                        })}
                    />
                </Grid>
                <Grid
                    className={classes.userContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.userHeaderContainer}
                        container
                        direction='row'
                        spacing={0}
                    >
                        <Grid
                            item
                            xs={10}
                        >
                            <Grid
                                container
                                direction='column'
                                wrap='nowrap'
                            >
                                <Grid
                                    alignItems='center'
                                    className={classes.userDetailContainer}
                                    container
                                    direction='row'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <Typography
                                        className={classes.userFullName}
                                        variant='h4'
                                    >{withdrawalInstruction.Description}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        {userRole.IsManageFirmAllowed && (
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='flex-end'
                                spacing={0}
                                wrap='nowrap'
                                xs={2}
                            >
                                <ActionButton
                                    className={classes.actionButton}
                                    handleClick={() => editWithdrawalInstruction && editWithdrawalInstruction(withdrawalInstruction,
                                        () => deleteWithdrawalInstructionValidation)}
                                    Icon={EditIcon}
                                    id='firmEditWithdrawalInstructionButton'
                                    text='Edit'
                                    type='icon'
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Tabs
                        classes={{ indicator: classes.indicator }}
                        className={classes.userTabsContainer}
                        onChange={(e, newTab) => setCurrentTab(newTab)}
                        value={currentTab}
                        variant='fullWidth'
                    >
                        <Tab
                            className={classes.userTab}
                            label='Details'
                        />
                        <Tab
                            className={classes.userTab}
                            label='Associated Accounts'
                        />
                    </Tabs>
                    <Grid
                        item
                        xs={12}
                    >
                        <TabPanel
                            index={0}
                            value={currentTab}
                        >
                            <>
                                <WithdrawalInstructionDetails
                                    withdrawalInstruction={withdrawalInstruction}
                                />
                            </>
                        </TabPanel>
                        <TabPanel
                            index={1}
                            value={currentTab}
                        >
                            <FirmWithdrawalWallets
                                firm={firm}
                                userRole={userRole}
                                withdrawalInstruction={withdrawalInstruction}
                            />
                        </TabPanel>
                    </Grid>

                </Grid>
            </Grid>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
}